import { ReactElement, useEffect, lazy, useMemo, HTMLAttributes } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { inject } from 'mobx-react';

import reload from '../../lib/reload';
import { Data } from '../../models/data';
import intercom from '../../lib/Intercom';
import { usePartnerConfig } from '../../contexts/partnerConfig';
import { Headers } from '../../components/Headers';
import MoneytreeIcon from '../../components/MoneytreeIcon';
import { TextButton } from '../../components/TextButton';
import { AnonymousArea } from '../../components/AnonymousArea';

import './styles.scss';

const BrandConnection = lazy(() => import('../../components/BrandConnection'));

function openIntercom() {
  intercom.open();
}

type Props = HTMLAttributes<HTMLDivElement>;

interface InjectedProps extends Props {
  dataModel: Data;
}

function MaintenanceView(props: Props): ReactElement {
  const {
    className = '',
    dataModel: {
      brandingInfo: { clientId, clientName = 'My Account' }
    },
    ...remainingProps
  } = props as InjectedProps;

  const { t } = useTranslation();
  const { config } = usePartnerConfig();

  useEffect(() => {
    const timeoutId = reload.init();

    return () => {
      clearTimeout(timeoutId);
      reload.clear();
    };
  }, []);

  const intercomButton = useMemo(
    () => (
      <TextButton
        big={true}
        className="maintenance-view-button"
        text={t('MaintenanceView.contactSupportButton')}
        onPress={openIntercom}
      />
    ),
    []
  );

  return (
    <AnonymousArea
      {...remainingProps}
      className={`${className} maintenance-view`}
    >
      <Headers />
      <div className="maintenance-view-content">
        {!clientId && <MoneytreeIcon width="180px" height="180px" />}
        {clientId && <BrandConnection clientId={clientId} />}
        <div className="maintenance-view-title">
          {t(`MaintenanceView.title`, {
            clientName: clientName || 'Moneytree'
          })}
        </div>
        <p className="maintenance-view-description">
          <Trans i18nKey="MaintenanceView.description">
            <br />
          </Trans>
        </p>
        {config?.enableIntercom &&
          Boolean(intercom.hasClient()) &&
          intercomButton}
      </div>
    </AnonymousArea>
  );
}

export default inject('dataModel')(MaintenanceView);
